// Constants
import { ModalType } from "@ms/uno-constants/lib/local/ModalType";
import { SubRouteType } from "@ms/uno-constants/lib/local/RoutingConstants";
// Actions
import { ActionType } from "@ms/planner-actions/lib/local/ActionType";
import { TaskAccessContext } from "@ms/planner-models/lib/local/client/planner/Enumerations";
// Stores
import { EventPublishingStore } from "@ms/uno-fluxcore/lib/local/stores/EventPublishingStore";
import { getTaskIdFromSubRoute } from "@ms/uno-routing/lib/local/utilities/RouteUtilities";
import { isEqual } from "@ms/uno-utilities/lib/local/ObjectUtilities";
import findIndex from "lodash/findIndex";
import isEmpty from "lodash/isEmpty";
import remove from "lodash/remove";
import { logUnreachableAction } from "../../utilities/LoggingUtilities";
import { Store } from "../Store";
/**
 * Store for Modal data
 */ export class ModalStore extends EventPublishingStore {
    get name() {
        return Store.Modal;
    }
    getCurrentModalData() {
        const topDialogDataIndex = this.modalDataLayers.length - 1;
        return this.modalDataLayers[topDialogDataIndex];
    }
    getCurrentModalDataLayers() {
        return this.modalDataLayers;
    }
    handleAction(action) {
        let storeChanged = false;
        switch(action.type){
            case ActionType.CloseModalAction:
                storeChanged = this.closeModal(action);
                break;
            case ActionType.OpenModalAction:
                storeChanged = this.setModal(action);
                break;
            case ActionType.NavigateToView:
                storeChanged = this.handleNavigateToViewAction(action);
                break;
            default:
                logUnreachableAction(this, this.loggers, action);
                return;
        }
        if (storeChanged) {
            this.publishStoreChangedEvent();
        }
    }
    /**
     * Handles the navigate to view actions
     * @param {INavigateToViewAction} action The navigate to view action
     */ handleNavigateToViewAction(action) {
        const storeChanged = this.closeModalOnPageNavigation();
        // Handle task deep links on navigation
        const taskEditorOpened = this.openTaskEditorModalFromRoute(action.routeChangeDescriptor);
        return storeChanged || taskEditorOpened;
    }
    /**
     * Remove modal data
     * @param action The ICloseModalAction with additional payload data for closing a particular modal
     */ closeModal(action) {
        let storeChanged = false;
        const modalData = action.modalData;
        // Remove all dialogs of the same type as the dialog to close
        const removedDialogs = remove(this.modalDataLayers, (modal)=>modal.modalType !== ModalType.None && modal === modalData);
        storeChanged = !isEmpty(removedDialogs);
        return storeChanged;
    }
    /**
     * Set modal data
     * @param action The IOpenModalAction with additional payload data for opening a particular modal
     */ setModal(action) {
        let storeChanged = false;
        const modalDataType = action.modalData;
        if (this.configProvider().flights.EnableTaskEditorRouting && modalDataType["taskId"]) {
            // Remove any existing dialogs that belong to a different task
            const removedModals = remove(this.modalDataLayers, (modal)=>{
                return modal["taskId"] && modal["taskId"] !== modalDataType["taskId"];
            });
            storeChanged = !isEmpty(removedModals);
        }
        const topDialogData = this.getCurrentModalData();
        // If equalityComparison is defined, use it to determine if the dialogs are the same
        const areDialogsTheSame = modalDataType.equalityComparison ? this.isDuplicateModal(modalDataType) : isEqual(topDialogData, modalDataType);
        if (!areDialogsTheSame) {
            if (modalDataType.modalType === ModalType.None) {
                // Remove all dialogs
                this.modalDataLayers = [
                    {
                        modalType: ModalType.None
                    }
                ];
            } else {
                this.modalDataLayers.push(modalDataType);
            }
            storeChanged = true;
        }
        return storeChanged;
    }
    /**
     * Opens the Task Editor Modal if the route contains a taskId and the modal is not already open.
     * Skips opening for legacy premium plans which doesn't use the new task editor.
     * @param route The route descriptor containing subRoute and subRouteType
     * @returns true if the modal was opened, false otherwise
     */ openTaskEditorModalFromRoute(route) {
        if (route.subRouteType === SubRouteType.PremiumPlan) {
            return false;
        }
        const taskId = getTaskIdFromSubRoute(route.subRoute);
        const containsTaskEditorModal = this.modalDataLayers.some((modal)=>modal.modalType === ModalType.TaskEditor && modal.taskId && modal.taskId === taskId);
        if (taskId && !containsTaskEditorModal) {
            // Render Task Editor Modal if route contains taskId
            const taskEditorModalData = {
                modalType: ModalType.TaskEditor,
                taskAccessContext: TaskAccessContext.Plan,
                taskId: taskId
            };
            this.modalDataLayers.push(taskEditorModalData);
            return true;
        }
        return false;
    }
    /**
     * Method to close the creation modal after an entity has been created
     */ closeModalOnPageNavigation() {
        let storeChanged = false;
        const disablePaneCloseOnNavigation = this.configProvider().changeGates.DisableLeaveCopilotPaneOpenOnNavigation;
        // Close all dialogs upon navigation except the app level dialogs.
        // This is done to ensure the page specific (Plan/MyDay/MyTasks) dialogs does not stick as the user move to different pages.
        if (!this.configProvider().changeGates.DisableDefaultModalCloseOnNavigation) {
            const modalsToRemove = remove(this.modalDataLayers, (modal)=>modal.modalType !== ModalType.None && modal.modalType !== ModalType.TryPremium && modal.modalType !== ModalType.UpsellStatus && modal.modalType !== ModalType.RequestPremiumLicense && modal.modalType !== ModalType.Info && modal.modalType !== ModalType.AppInfo && modal.modalType !== ModalType.AuthError && modal.modalType !== ModalType.FRE && modal.modalType !== ModalType.NotificationSettingsDialog && (disablePaneCloseOnNavigation || modal.modalType !== ModalType.CopilotSidecar));
            storeChanged = !isEmpty(modalsToRemove);
        } else {
            const modalsToRemove = remove(this.modalDataLayers, (modal)=>modal.modalType === ModalType.CreatePlan || modal.modalType === ModalType.SelectPlan || modal.modalType === ModalType.ConvertPlan || modal.modalType === ModalType.CreatePortfolio || disablePaneCloseOnNavigation && modal.modalType === ModalType.CopilotSidecar);
            storeChanged = !isEmpty(modalsToRemove);
        }
        // Clear queryToExecuteOnLoad value for CopilotSidecar so it does not persist across navigation
        this.modalDataLayers = this.modalDataLayers.map((modal)=>{
            if (modal.modalType === ModalType.CopilotSidecar && modal["queryToExecuteOnLoad"]) {
                storeChanged = true;
                return {
                    ...modal,
                    queryToExecuteOnLoad: undefined
                };
            }
            return modal;
        });
        return storeChanged;
    }
    /**
     * Method to check if the dialog is a duplicate
     * @param modalDataType The modal data type to check
     */ isDuplicateModal(modalDataType) {
        const index = findIndex(this.modalDataLayers, (modal)=>{
            return modalDataType.equalityComparison?.(modal) ?? false;
        });
        return index !== -1;
    }
    constructor(initialRoute, loggers, configProvider){
        super(), this.initialRoute = initialRoute, this.loggers = loggers, this.configProvider = configProvider;
        this.modalDataLayers = [
            {
                modalType: ModalType.None
            }
        ];
        // Temporary change to get more feedback
        // Will not be rolled out to production
        if (configProvider().flights.EnableMyTasksCopilotPaneOpenOnLoad && configProvider().settings.EnvironmentFeatureSettings.EnableProjectManagerExperience) {
            const modalData = {
                modalType: ModalType.CopilotSidecar,
                pageId: SubRouteType.MyTasks
            };
            this.modalDataLayers.push(modalData);
        }
        this.openTaskEditorModalFromRoute(this.initialRoute);
    }
}
